/**
 * 3D Electrical-Room view — orchestrator.
 * Scene parts live in /components/r3d/scene.js (createElement-only).
 * UI parts (toolbar, side panel, report) live in /components/r3d/*.jsx.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Cube, ArrowsOut, Play, ArrowsClockwise, Ruler,
  SelectionAll, Brain, Lock, House,
} from "@phosphor-icons/react";

import { Scene, IN_TO_M } from "@/components/r3d/scene";
import { orientedDimensions } from "@/lib/orientation";
import EquipmentSidePanel from "@/components/r3d/EquipmentSidePanel";
import R3DToolbar from "@/components/r3d/Toolbar";
import ValidationReport from "@/components/r3d/ValidationReport";
import AIAuditModal from "@/components/r3d/AIAuditModal";
import HelpModal from "@/components/r3d/HelpModal";
import OpeningsEditor from "@/components/r3d/OpeningsEditor";
import useClashDetection from "@/components/r3d/useClashDetection";
import { useStagedItems, useComplianceById } from "@/components/r3d/useSceneProjection";
import ContextMenu from "@/components/ContextMenu";
import QuickJoinDialog from "@/components/trays/QuickJoinDialog";
import WireToSourceDialog from "@/components/WireToSourceDialog";

const h = React.createElement;

export default function Room3DView({ project, update, projectId, onJumpToTab }) {
  const [selected, setSelected] = useState(null);
  const [selectedSet, setSelectedSet] = useState(() => new Set());     // ids of equipment in multi-select
  const [isolate, setIsolate] = useState(false);
  const [selectedTrayId, setSelectedTrayId] = useState(null);
  const [report, setReport] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [snap, setSnap] = useState(12);
  const [measureMode, setMeasureMode] = useState(false);
  const [measurePicks, setMeasurePicks] = useState([]);
  const [sidePanelOpen, setSidePanelOpen] = useState(true);
  const [focusTarget, setFocusTarget] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTrays, setShowTrays] = useState(true);
  const [showConduits, setShowConduits] = useState(false);
  const [showDimensions, setShowDimensions] = useState(false);
  const [showShell, setShowShell] = useState(false);
  // v2.13 — Shell view presets now WRITE hidden_walls into the project so
  // wall hide/show goes through the standard undo stack. Preset state is
  // local UI state for the active-tab indicator.
  const [shellPreset, setShellPresetState] = useState(null);
  const presetHiddenWalls = (preset) => {
    if (preset === "presentation") return ["C"];   // hide front wall as cutaway
    if (preset === "structural")   return [];      // all walls visible
    return null;
  };
  const setShellPreset = (preset) => {
    setShellPresetState(preset);
    if (preset === "edit") {
      setShowShell(false);
    } else if (preset === "structural" || preset === "presentation") {
      setShowShell(true);
      update({ hidden_walls: presetHiddenWalls(preset) });
    }
  };
  // v2.13 — onToggleWall is called from the Room mesh onContextMenu
  // (right-click). It toggles membership in project.hidden_walls and
  // delegates to `update()` so the change lands in the undo stack.
  const onToggleWall = (wallName) => {
    const cur = project.hidden_walls || [];
    const next = cur.includes(wallName)
      ? cur.filter((w) => w !== wallName)
      : [...cur, wallName];
    update({ hidden_walls: next });
    // Clear preset highlight whenever the user customises mid-preset —
    // makes it obvious the view is now bespoke.
    setShellPresetState(null);
  };
  const [showWallGrids, setShowWallGrids] = useState(false);
  const [showWorkingSpace, setShowWorkingSpace] = useState(false);
  const [showFeederLines, setShowFeederLines] = useState(true);
  const [openingsOpen, setOpeningsOpen] = useState(false);
  const [autoValidate, setAutoValidate] = useState(true);
  const [viewLocked, setViewLocked] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [fillBySegment, setFillBySegment] = useState({});
  const [dragging, setDragging] = useState(false);
  // Lock equipment in place while the user edits trays/conduits. Auto-engaged
  // when Trays toggle flips ON (see effect below). User can override.
  const [editingTrays, setEditingTrays] = useState(false);
  const [equipmentLocked, setEquipmentLocked] = useState(false);

  // Right-click menu (Duplicate · Delete · Suggest fitting) — works on both
  // 3D equipment boxes and tray meshes.  The menu shape is `{x, y, item?, seg?}`
  // and the kind drives which menu items render.
  const [ctxMenu, setCtxMenu] = useState(null);
  const [quickJoinSrc, setQuickJoinSrc] = useState(null);
  const [wireSourceItem, setWireSourceItem] = useState(null);

  const onEquipmentContextMenu = (e, item) => {
    const native = e.nativeEvent || e;
    native.preventDefault && native.preventDefault();
    setCtxMenu({ x: native.clientX || 0, y: native.clientY || 0, item });
  };
  const onTrayContextMenu = ({ x, y, seg }) => {
    setCtxMenu({ x, y, seg });
  };

  const duplicateEquipment = async () => {
    const item = ctxMenu?.item;
    if (!item?.id) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/equipment-list/${item.id}/duplicate`);
      update && update({ equipment_list: [...(project.equipment_list || []), data] });
      toast.success(`Duplicated ${data.tag}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Duplicate failed");
    }
  };
  const wireToSource = () => {
    // Open the picker so the user can choose ANY positioned equipment.
    if (ctxMenu?.item) setWireSourceItem(ctxMenu.item);
  };
  const clearSource = async () => {
    const item = ctxMenu?.item;
    if (!item?.id) return;
    try {
      const { data } = await api.post(
        `/projects/${projectId}/equipment-list/${item.id}/clear-source`,
      );
      const list = (project.equipment_list || []).map(
        (x) => x.id === item.id ? data.item : x,
      );
      update && update({ equipment_list: list });
      toast.success(`Cleared feeder link on ${item.tag}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clear feeder failed");
    }
  };
  const deleteEquipment = async () => {
    const item = ctxMenu?.item;
    if (!item?.id) return;
    try {
      await api.delete(`/projects/${projectId}/equipment-list/${item.id}`);
      update && update({ equipment_list: (project.equipment_list || []).filter((x) => x.id !== item.id) });
      toast.success(`Deleted ${item.tag || item.equipment_type}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };
  const duplicateTray = async () => {
    const seg = ctxMenu?.seg;
    if (!seg?.id) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/trays/${seg.id}/duplicate`);
      update && update({ cable_trays: [...(project.cable_trays || []), data] });
      toast.success(`Duplicated ${data.id_display || data.id.slice(0, 6)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Duplicate failed");
    }
  };
  const deleteTray = async () => {
    const seg = ctxMenu?.seg;
    if (!seg?.id) return;
    try {
      await api.delete(`/projects/${projectId}/trays/${seg.id}`);
      update && update({ cable_trays: (project.cable_trays || []).filter((x) => x.id !== seg.id) });
      toast.success(`Deleted tray ${seg.id_display || seg.id.slice(0, 6)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };
  const rotateTray = async (segId) => {
    if (!segId) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/trays/${segId}/rotate`);
      update && update({
        cable_trays: (project.cable_trays || []).map(
          (x) => x.id === segId ? { ...x, rotation_axis_deg: data.rotation_axis_deg } : x,
        ),
      });
      toast.success(`Rotated ${data.id_display || segId.slice(0, 6)} → ${data.rotation_axis_deg}°`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rotate failed");
    }
  };
  const quickJoinTray = () => {
    if (ctxMenu?.seg) setQuickJoinSrc(ctxMenu.seg);
  };
  /** AI auto-tag — asks Claude for a conduit-type recommendation for the
   *  right-clicked tray run, then patches `conduit_type` on the segment
   *  so the 3D `ConduitLayer` immediately renders the horizontal cylinder.
   *  Debounced with a `useRef` busy flag so rapid double-clicks don't
   *  fire parallel Claude calls (each ~$0.01 + 3s latency). */
  const suggestingConduitRef = useRef(false);
  const suggestConduit = async () => {
    const seg = ctxMenu?.seg;
    if (!seg) return;
    if (suggestingConduitRef.current) return;
    suggestingConduitRef.current = true;
    const tid = toast.loading("AI sizing a conduit for this run…");
    try {
      const { data } = await api.post(
        `/projects/${projectId}/trays/${seg.id}/suggest-conduit-type`,
      );
      const ct = data.conduit_type;
      const just = data.justification || "";
      const apply = window.confirm(
        `Suggested: ${ct}\n\n${just}\n\nApply ${ct} to this tray run?`,
      );
      if (apply) {
        const next = (project.cable_trays || []).map((t) =>
          t.id === seg.id ? { ...t, conduit_type: ct } : t,
        );
        update({ cable_trays: next });
        toast.success(`Applied ${ct} to ${seg.id_display || seg.id.slice(0, 6)}`, { id: tid });
      } else {
        toast.dismiss(tid);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI conduit suggestion failed", { id: tid });
    } finally {
      suggestingConduitRef.current = false;
    }
  };
  // E2E hook — Playwright can call window.__quickjoin_open_for_test(trayId)
  // to open the QuickJoinDialog directly without choreographing a 3D
  // right-click. Looks up the tray segment from project.cable_trays.
  useEffect(() => {
    window.__quickjoin_open_for_test = (trayId) => {
      const seg = (project.cable_trays || []).find((t) => t.id === trayId);
      if (!seg) return false;
      setQuickJoinSrc(seg);
      return true;
    };
    return () => { delete window.__quickjoin_open_for_test; };
  }, [project.cable_trays]);
  const wrapRef = useRef(null);
  const fullscreenRef = useRef(null);
  const validateTimerRef = useRef(null);

  // When trays become visible, default to lock-on (option a from spec). When
  // trays are hidden, re-enable equipment editing.
  useEffect(() => {
    if (showTrays) {
      setEditingTrays(true);
      setEquipmentLocked(true);
    } else {
      setEditingTrays(false);
      setEquipmentLocked(false);
    }
  }, [showTrays]);

  const noRoom = !project.length_in || !project.width_in;
  const loadList = project.equipment_list || [];

  const itemsToRender = useStagedItems(project, { isolate, selected, selectedSet });
  const complianceById = useComplianceById(report, loadList);
  const clashes = useClashDetection(project);

  const onToggleIgnore = (itemId, ignore) => {
    const next = loadList.map((it) =>
      it.id === itemId ? { ...it, clash_ignored: !!ignore } : it,
    );
    update({ equipment_list: next });
  };

  /** v1.62 — Snap-into-compliance. For a CLEARANCE clash between items A
   *  (the offending item) and B (the partner), compute the minimum nudge
   *  along the clash axis that brings the gap up to MIN_SIDE_CLEARANCE_IN
   *  (= NEC 110.26(A) 36"). Push item A away from B, clamped to room bounds.
   *  Returns false (with a toast) if room is too tight to satisfy the rule.
   *  v1.63 — also resolves OVERLAP clashes by pushing along the smaller
   *  penetration axis until the boxes no longer overlap PLUS 36" clearance. */
  const onSnapToCompliance = (clash) => {
    if (!clash || (clash.code !== "CLEARANCE" && clash.code !== "OVERLAP")) {
      toast.message("Snap-to-compliance only resolves footprint conflicts");
      return;
    }
    const MIN_CLEAR = 36;
    const a = loadList.find((x) => x.id === clash.item_id);
    const b = loadList.find((x) => x.id === clash.partner_id);
    if (!a || !b || !a.position || !b.position) return;
    const aOd = orientedDimensions(a);
    const bOd = orientedDimensions(b);
    const aw = aOd.wallSize, ad = aOd.thickness;
    const bw = bOd.wallSize, bd = bOd.thickness;
    const ap = a.position, bp = b.position;
    const rL = project.length_in || 240;
    const rW = project.width_in  || 180;

    let axis = clash.axis, needed = MIN_CLEAR - (Number(clash.gap) || 0);
    if (clash.code === "OVERLAP") {
      // Pick the axis with the SMALLER penetration so we move the least.
      const penX = Math.min(ap.x_in + aw, bp.x_in + bw) - Math.max(ap.x_in, bp.x_in);
      const penY = Math.min(ap.y_in + ad, bp.y_in + bd) - Math.max(ap.y_in, bp.y_in);
      axis = (penX <= penY) ? "x" : "y";
      // Need the penetration distance to undo + 36" clearance on top.
      needed = (axis === "x" ? penX : penY) + MIN_CLEAR;
    }
    if (needed <= 0) return;

    let newX = ap.x_in, newY = ap.y_in;
    if (axis === "x") {
      const dir = (ap.x_in + aw / 2) < (bp.x_in + bw / 2) ? -1 : 1;
      newX = Math.max(0, Math.min(rL - aw, ap.x_in + dir * needed));
    } else if (axis === "y") {
      const dir = (ap.y_in + ad / 2) < (bp.y_in + bd / 2) ? -1 : 1;
      newY = Math.max(0, Math.min(rW - ad, ap.y_in + dir * needed));
    }

    // Verify the nudge actually closes the gap (room may be too tight).
    const achievedX = axis === "x" ? Math.abs((newX + aw / 2) - (bp.x_in + bw / 2)) - (aw + bw) / 2 : Infinity;
    const achievedY = axis === "y" ? Math.abs((newY + ad / 2) - (bp.y_in + bd / 2)) - (ad + bd) / 2 : Infinity;
    const achieved = axis === "x" ? achievedX : achievedY;
    if (achieved < MIN_CLEAR) {
      toast.warning(`Room too tight — could only achieve ${achieved.toFixed(0)}" (need ≥ ${MIN_CLEAR}"). Moved as far as possible.`);
    } else {
      toast.success(`${a.tag} nudged ${needed.toFixed(0)}" to satisfy NEC 110.26(A)`);
    }

    const next = loadList.map((it) =>
      it.id === a.id
        ? { ...it, position: { ...it.position, x_in: newX, y_in: newY } }
        : it,
    );
    update({ equipment_list: next });
  };

  /** v1.63 — Resolve every CLEARANCE + OVERLAP clash sequentially. Each
   *  iteration we pick the most painful clash, snap it, then recompute.
   *  Bail out after 10 iterations to avoid infinite oscillation in tight
   *  rooms. */
  const onSnapAll = () => {
    const resolvable = clashes.filter((c) =>
      !c.ignored && (c.code === "CLEARANCE" || c.code === "OVERLAP") && c.partner_id,
    );
    if (resolvable.length === 0) {
      toast.message("Nothing to auto-resolve — all clear ✓");
      return;
    }
    // Build the final equipment_list by applying each snap in turn against a
    // mutable working copy. Avoids React batched-state issues from looping
    // onSnapToCompliance() (which sets project state on each call).
    let working = loadList.map((it) => ({ ...it }));
    let resolved = 0, skipped = 0;
    const MAX_ITERS = 10;
    for (let iter = 0; iter < MAX_ITERS; iter += 1) {
      // Recompute clashes against the working list (simple recreation).
      const fakeProj = { ...project, equipment_list: working };
      // Local detector — re-uses orientedDimensions + 36" rule. Returns
      // the first resolvable clash, or null when nothing remains.
      const findOne = () => {
        const MIN_CLEAR = 36;
        for (let i = 0; i < working.length; i += 1) {
          for (let j = i + 1; j < working.length; j += 1) {
            const a = working[i], b = working[j];
            if (!a.position || !b.position) continue;
            if (a.clash_ignored || b.clash_ignored) continue;
            const ao = orientedDimensions(a), bo = orientedDimensions(b);
            const aw = ao.wallSize, ad = ao.thickness, bw = bo.wallSize, bd = bo.thickness;
            const xOv = !(a.position.x_in + aw <= b.position.x_in || b.position.x_in + bw <= a.position.x_in);
            const yOv = !(a.position.y_in + ad <= b.position.y_in || b.position.y_in + bd <= a.position.y_in);
            // Vertical bands
            const az0 = +a.mount_height_in || 0, az1 = az0 + ao.vertSize;
            const bz0 = +b.mount_height_in || 0, bz1 = bz0 + bo.vertSize;
            if (az1 <= bz0 || bz1 <= az0) continue;
            if (xOv && yOv) {
              const penX = Math.min(a.position.x_in + aw, b.position.x_in + bw) - Math.max(a.position.x_in, b.position.x_in);
              const penY = Math.min(a.position.y_in + ad, b.position.y_in + bd) - Math.max(a.position.y_in, b.position.y_in);
              return { code: "OVERLAP", a, b, axis: penX <= penY ? "x" : "y", needed: (penX <= penY ? penX : penY) + MIN_CLEAR };
            }
            if (yOv) {
              const gapX = a.position.x_in + aw <= b.position.x_in ? b.position.x_in - (a.position.x_in + aw)
                          : b.position.x_in + bw <= a.position.x_in ? a.position.x_in - (b.position.x_in + bw)
                          : -1;
              if (gapX >= 0 && gapX < MIN_CLEAR) return { code: "CLEARANCE", a, b, axis: "x", needed: MIN_CLEAR - gapX };
            }
            if (xOv) {
              const gapY = a.position.y_in + ad <= b.position.y_in ? b.position.y_in - (a.position.y_in + ad)
                          : b.position.y_in + bd <= a.position.y_in ? a.position.y_in - (b.position.y_in + bd)
                          : -1;
              if (gapY >= 0 && gapY < MIN_CLEAR) return { code: "CLEARANCE", a, b, axis: "y", needed: MIN_CLEAR - gapY };
            }
          }
        }
        return null;
      };
      const c = findOne();
      if (!c) break;
      // Apply the nudge on item A (move it, leave B fixed).
      const { a, b, axis, needed } = c;
      const ao = orientedDimensions(a);
      const rL = fakeProj.length_in || 240;
      const rW = fakeProj.width_in  || 180;
      let newX = a.position.x_in, newY = a.position.y_in;
      if (axis === "x") {
        const dir = (a.position.x_in + ao.wallSize / 2) < (b.position.x_in + orientedDimensions(b).wallSize / 2) ? -1 : 1;
        newX = Math.max(0, Math.min(rL - ao.wallSize, a.position.x_in + dir * needed));
      } else {
        const dir = (a.position.y_in + ao.thickness / 2) < (b.position.y_in + orientedDimensions(b).thickness / 2) ? -1 : 1;
        newY = Math.max(0, Math.min(rW - ao.thickness, a.position.y_in + dir * needed));
      }
      // No movement possible (wall blocked) → bail to avoid infinite loop.
      if (newX === a.position.x_in && newY === a.position.y_in) {
        skipped += 1;
        // Mark this clash as effectively done to break the loop. Real
        // production fix would try moving B instead — left as future work.
        a.clash_ignored = true;
        continue;
      }
      working = working.map((it) => it.id === a.id
        ? { ...it, position: { ...it.position, x_in: newX, y_in: newY } }
        : it);
      resolved += 1;
    }
    if (resolved) {
      update({ equipment_list: working });
      toast.success(`Auto-resolved ${resolved} clash${resolved === 1 ? "" : "es"}${skipped ? ` (${skipped} blocked by walls)` : ""}`);
    } else if (skipped) {
      toast.warning(`Couldn't auto-resolve — ${skipped} clash${skipped === 1 ? "" : "es"} blocked by room walls`);
    }
  };

  const validate = async ({ silent = false } = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/layout/validate`);
      setReport(data);
      if (!silent) {
        if (data.compliant) toast.success("Design is NEC 110.26 compliant");
        else toast.warning(`Found ${data.summary.errors} error(s), ${data.summary.warnings} warning(s)`);
      }
    } catch (err) {
      console.error("Validation failed:", err);
      if (!silent) toast.error("Validation failed");
    } finally { setBusy(false); }
  };

  const scheduleAutoValidate = () => {
    if (!autoValidate) return;
    if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    validateTimerRef.current = setTimeout(() => validate({ silent: true }), 600);
  };

  useEffect(() => () => {
    if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
  }, []);

  // v2.13 — ESC cancels any active 3D mode (measure, edit-openings) and
  // clears the current selection. Matches the SizeTab ESC behaviour so
  // both views share the same muscle memory.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" && e.key !== "Esc") return;
      const t = e.target;
      const tag = (t?.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) return;
      if (measureMode) {
        setMeasureMode(false);
        setMeasurePicks([]);
        return;
      }
      if (selected || (selectedSet && selectedSet.size) || selectedTrayId) {
        setSelected(null);
        setSelectedSet(new Set());
        setSelectedTrayId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [measureMode, selected, selectedSet, selectedTrayId]);

  const handleDragEnd = async (itemId, newX, newY) => {
    const next = loadList.map((it) => {
      if (it.id !== itemId) return it;
      const cur = it.position || {};
      return {
        ...it,
        position: {
          ...cur, x_in: newX, y_in: newY,
          w_in: cur.w_in || it.length_in || 24,
          h_in: cur.h_in || it.width_in  || 18,
        },
      };
    });
    update({ equipment_list: next });
    if (!autoValidate) setReport(null);
    scheduleAutoValidate();
  };

  /* Drag a tray endpoint in the 3D scene — patches cable_trays[*].points
     locally so the rebuild is immediate, then debounced-saves to PATCH. */
  const traySaveTimerRef = useRef();
  const handleMoveTrayEndpoint = (segId, pointIdx, [nx, ny]) => {
    const next = (project.cable_trays || []).map((s) => {
      if (s.id !== segId) return s;
      const points = s.points.map((p, i) => (i === pointIdx ? [nx, ny] : p));
      return { ...s, points };
    });
    update({ cable_trays: next });
    if (traySaveTimerRef.current) clearTimeout(traySaveTimerRef.current);
    traySaveTimerRef.current = setTimeout(() => {
      // Save through the existing tray editor PUT to keep deliverables in sync.
      api.put(`/projects/${projectId}/trays`, { segments: next })
         .catch((err) => console.warn("Tray save failed:", err));
    }, 400);
    if (autoValidate) scheduleAutoValidate();
  };

  const handleMeasurePick = (item) =>
    setMeasurePicks((prev) => (prev.length >= 2 ? [item] : [...prev, item]));

  const clearMeasure = () => { setMeasurePicks([]); setMeasureMode(false); };

  const onFocus = (item, additive = false) => {
    if (additive) {
      // Toggle in/out of selection set
      setSelectedSet((prev) => {
        const next = new Set(prev);
        next.has(item.id) ? next.delete(item.id) : next.add(item.id);
        return next;
      });
      setSelected(item.id);
      return;
    }
    setSelected(item.id);
    setSelectedSet(new Set([item.id]));
    setFocusTarget({ ...item, _ts: Date.now() });
  };

  /* Tray-focus is now opt-in: the side-panel "Focus" button calls
   * `focusOnTray(seg)` directly.  Clicking a tray in the scene only
   * toggles selection — the camera no longer flies, so the user keeps
   * whatever view they already framed. */
  const traysRef = useRef(project.cable_trays);
  traysRef.current = project.cable_trays;
  const focusOnTray = useCallback((seg) => {
    if (!seg || !seg.points || seg.points.length < 1) return;
    const room_H_in = project.depth_in || 96;
    const room_L_in = project.length_in || 240;
    const room_W_in = project.width_in  || 180;
    const ceilingMountIn = room_H_in - 12;
    const dropBottomIn = 78;
    const roomDiag_m = Math.hypot(room_L_in, room_W_in) * IN_TO_M;
    const offset_m = Math.max(3.0, roomDiag_m * 0.45);
    const [[x1, y1], pt2] = [seg.points[0], seg.points[1] || seg.points[0]];
    const [x2, y2] = pt2;
    if (seg.kind === "run") {
      const elev = (seg.elevation_in != null) ? seg.elevation_in : ceilingMountIn;
      setFocusTarget({ kind: "tray", _ts: Date.now(),
                        x_in: (x1 + x2) / 2, y_in: elev, z_in: (y1 + y2) / 2,
                        offset_m });
    } else {
      const top    = (seg.top_in    != null) ? seg.top_in    : ceilingMountIn;
      const bottom = (seg.bottom_in != null) ? seg.bottom_in : dropBottomIn;
      setFocusTarget({ kind: "tray", _ts: Date.now(),
                        x_in: x1, y_in: (top + bottom) / 2, z_in: y1,
                        offset_m });
    }
  }, [project.depth_in, project.length_in, project.width_in]);

  const toggleFullscreen = () => {
    const target = fullscreenRef.current || wrapRef.current;
    if (!target) return;
    if (!document.fullscreenElement) {
      target.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  /* Fetch cable schedule lazily so tray rails can colour by fill-pct. */
  useEffect(() => {
    if (!showTrays || !(project.cable_trays || []).length) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/cable-schedule`);
        if (cancel) return;
        const map = {};
        (data?.tray_fill?.segments || []).forEach((s) => { map[s.id] = s.fill_pct; });
        setFillBySegment(map);
      } catch (err) {
        console.warn("Tray fill colouring unavailable:", err);
      }
    })();
    return () => { cancel = true; };
  }, [showTrays, projectId, project.cable_trays, project.equipment_list, project.circuits]);

  const canvasEl = h(Canvas, {
    shadows: true,
    onContextMenu: (e) => e.preventDefault(),   // suppress browser menu — ours wins
    camera: {
      position: [
        (project.length_in || 240) * IN_TO_M * 1.2,
        (project.depth_in  || 96)  * IN_TO_M * 1.4,
        (project.width_in  || 180) * IN_TO_M * 1.2,
      ],
      fov: 45,
    },
    "data-testid": "r3d-canvas",
  }, h(Scene, {
    project, complianceById,
    onDragEnd: equipmentLocked ? () => {} : handleDragEnd,
    equipmentLocked,
    selected, setSelected,
    snap, measureMode, measurePicks, onMeasurePick: handleMeasurePick,
    focusTarget, itemsToRender, showTrays, showConduits, showDimensions,
    showFeederLines,
    onFeederClick: (dstTag) => {
      // Open Cables tab and emit a focus signal so the matching cable row
      // scrolls into view + flashes. CablesTab subscribes to this event.
      if (typeof onJumpToTab === "function") onJumpToTab("cables");
      window.dispatchEvent(new CustomEvent("eengdes:focus-cable", {
        detail: { tag: dstTag, ts: Date.now() },
      }));
    },
    selectedTrayId, setSelectedTrayId, viewLocked, fillBySegment,
    showShell, showWallGrids, openings: project.wall_openings || [],
    hiddenWalls: project.hidden_walls || [],
    onToggleWall,
    showWorkingSpace, dragging, setDragging,
    onMoveTrayEndpoint: handleMoveTrayEndpoint,
    onEquipmentContextMenu, onTrayContextMenu,
  }));

  return (
    <div data-testid="room-3d-view">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-tag">// 05 3D Layout</div>
          <h2 className="font-display text-2xl font-bold inline-flex items-center gap-2">
            <Cube size={26} /> 3D design &amp; validation
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Click items to select · drag boxes on floor · right-click for duplicate/delete/quick-join · ⌖ to focus camera
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            className={`btn-ghost text-sm py-2 inline-flex items-center gap-2 ${isolate ? "!bg-[#FFCC00]/20 !text-[#FFCC00] !border-[#FFCC00]/40" : ""}`}
            onClick={() => setIsolate(!isolate)}
            disabled={!selected && selectedSet.size === 0}
            title={isolate ? "Show all equipment" : "Hide all but the selected equipment"}
            data-testid="r3d-toggle-isolate"
          >
            <SelectionAll size={16} weight={isolate ? "fill" : "regular"} /> {isolate ? "Show all" : "Isolate"}
            {selectedSet.size > 1 && <span className="text-[10px] opacity-70">({selectedSet.size})</span>}
          </button>
          <button
            className="btn-ghost text-sm py-2 inline-flex items-center gap-2"
            onClick={() => setAuditOpen(true)}
            data-testid="r3d-ai-audit"
          >
            <Brain size={16} /> AI Audit
          </button>
          <button
            className="btn-ghost text-sm py-2 inline-flex items-center gap-2"
            onClick={() => setReport(null)} disabled={!report}
            data-testid="r3d-reset-validation"
          >
            <ArrowsClockwise size={16} /> Reset
          </button>
          <button
            className="btn-primary text-sm py-2 inline-flex items-center gap-2"
            onClick={validate}
            disabled={busy || noRoom || itemsToRender.filter((i) => !i._unplaced).length === 0}
            data-testid="r3d-validate"
          >
            <Play size={16} weight="bold" /> {busy ? "Validating…" : "Validate Design"}
          </button>
        </div>
      </div>

      <div ref={fullscreenRef} className={isFullscreen ? "bg-[#050608] p-3 h-screen overflow-auto" : ""} data-testid="r3d-fullscreen-container">
      {/* In fullscreen the toolbar is sticky to the top of the scroll
          container so it never disappears when the user pans/zooms or
          scrolls past the canvas. */}
      <div className={isFullscreen ? "sticky top-0 z-30 bg-[#050608] pb-2" : ""}>
      <R3DToolbar
        snap={snap} setSnap={setSnap}
        measureMode={measureMode} setMeasureMode={setMeasureMode}
        measurePicks={measurePicks} clearMeasure={clearMeasure}
        showTrays={showTrays} setShowTrays={setShowTrays}
        showConduits={showConduits} setShowConduits={setShowConduits}
        showDimensions={showDimensions} setShowDimensions={setShowDimensions}
        showShell={showShell} setShowShell={setShowShell}
        shellPreset={shellPreset} setShellPreset={setShellPreset}
        showWallGrids={showWallGrids} setShowWallGrids={setShowWallGrids}
        showWorkingSpace={showWorkingSpace} setShowWorkingSpace={setShowWorkingSpace}
        showFeederLines={showFeederLines} setShowFeederLines={setShowFeederLines}
        onEditOpenings={() => setOpeningsOpen(true)}
        autoValidate={autoValidate} setAutoValidate={setAutoValidate}
        viewLocked={viewLocked} setViewLocked={setViewLocked}
        onHelp={() => setHelpOpen(true)}
        sidePanelOpen={sidePanelOpen} setSidePanelOpen={setSidePanelOpen}
        isFullscreen={isFullscreen} toggleFullscreen={toggleFullscreen}
      />
      </div>

      {/* v2.11 — Shell hint. When the Shell is enabled, surface the
          right-click-to-hide affordance so users discover the feature
          without having to read docs. Disappears when Shell is off. */}
      {showShell && (
        <div
          className="border border-cyan-300/40 bg-cyan-500/5 px-3 py-2 mb-3 text-[11px] font-mono text-cyan-200 flex items-center gap-2"
          data-testid="r3d-shell-hint"
        >
          <House size={14} weight="fill" />
          <span>
            Shell is ON · <strong className="text-cyan-100">right-click any wall, the roof, the floor or any basement face</strong> in the 3D view to hide it. <kbd className="px-1 py-0.5 bg-black/40 border border-cyan-300/40">Ctrl+Z</kbd> restores hidden faces one at a time.
          </span>
        </div>
      )}

      {noRoom && (
        <div className="border border-yellow-500/40 bg-yellow-500/5 p-4 text-sm text-yellow-300 font-mono mb-4" data-testid="r3d-no-room">
          Set room length × width on the Size tab to enable the 3D view.
        </div>
      )}
      {!noRoom && loadList.length === 0 && (
        <div className="border border-cyan-500/30 bg-cyan-500/5 p-4 text-sm text-cyan-200 font-mono mb-4" data-testid="r3d-no-items">
          Add equipment on the Equipment tab (Equipment List) — they will appear here automatically.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div
          ref={wrapRef}
          className="relative border border-white/10 bg-[#050608]"
          style={{ height: isFullscreen ? "calc(100vh - 86px)" : 540 }}
          data-testid="r3d-canvas-wrapper"
        >
          {!noRoom ? canvasEl : null}

          {/* Lock-on-tray-edit banner */}
          {editingTrays && (
            <div
              className={`absolute top-2 left-2 z-20 text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 inline-flex items-center gap-2 cursor-pointer ${
                equipmentLocked
                  ? "bg-[#FFCC00] text-black"
                  : "bg-black/70 text-[#FFCC00] border border-[#FFCC00]"
              }`}
              onClick={() => setEquipmentLocked((v) => !v)}
              data-testid="r3d-equip-lock-banner"
              title="Equipment is locked while editing trays. Click to toggle."
            >
              <Lock size={11} weight={equipmentLocked ? "fill" : "regular"} />
              {equipmentLocked
                ? "Equipment locked — editing trays. Click to unlock."
                : "Equipment unlocked. Click to re-lock while editing trays."}
            </div>
          )}

          {measureMode && (
            <div className="absolute top-2 left-2 bg-[#00E5FF] text-black text-[10px] font-mono uppercase tracking-wider px-2 py-1 z-10" data-testid="r3d-measure-banner">
              <Ruler size={11} className="inline mr-1" />
              Measurement mode — pick {2 - measurePicks.length} more {measurePicks.length === 1 ? "item" : "items"}
            </div>
          )}

          <div className="absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.2em] text-gray-500 font-mono pointer-events-none">
            <ArrowsOut size={12} className="inline mr-1" />
            Drag empty space to orbit · drag equipment to move · scroll to zoom · right-click to pan
          </div>

          {sidePanelOpen && !noRoom && (
            <EquipmentSidePanel
              items={loadList}
              onFocus={onFocus}
              onClose={() => setSidePanelOpen(false)}
              selected={selected}
              selectedSet={selectedSet}
              showDimensions={showDimensions}
              room={{
                length_in: project.length_in || 240,
                width_in:  project.width_in  || 180,
                depth_in:  project.depth_in  || 96,
              }}
              update={update}
              trays={project.cable_trays || []}
              selectedTrayId={selectedTrayId}
              setSelectedTrayId={setSelectedTrayId}
              onTrayFocus={focusOnTray}
              onTrayRotate={(id) => rotateTray(id)}
              onTrayRename={async (id, newId) => {
                const next = (project.cable_trays || []).map((t) =>
                  t.id === id ? { ...t, id_display: newId } : t,
                );
                // Optimistic: update local state immediately.
                update({ cable_trays: next });
                // CRITICAL: also persist via the /trays endpoint so the
                // rename survives sign-out without requiring the user to
                // also click "Save Project". (Without this, the parent's
                // dirty flag would track the change but the DB stays stale.)
                try {
                  await api.put(`/projects/${projectId}/trays`, {
                    segments: next,
                    largest_cable_od_in: project.largest_cable_od_in,
                    cable_weight_lb_per_ft: project.cable_weight_lb_per_ft,
                    default_product_sku: project.tray_design_meta?.default_product_sku,
                    default_load_class:  project.tray_design_meta?.default_load_class,
                  });
                } catch (e) {
                  toast.error("Rename failed to save — please click Save Project.");
                  console.error("Tray rename persist failed:", e);
                }
              }}
              onTrayDelete={async (id) => {
                const next = (project.cable_trays || []).filter((t) => t.id !== id);
                update({ cable_trays: next });
                try {
                  await api.put(`/projects/${projectId}/trays`, {
                    segments: next,
                    largest_cable_od_in: project.largest_cable_od_in,
                    cable_weight_lb_per_ft: project.cable_weight_lb_per_ft,
                    default_product_sku: project.tray_design_meta?.default_product_sku,
                    default_load_class:  project.tray_design_meta?.default_load_class,
                  });
                } catch (e) {
                  toast.error("Delete failed to save — please click Save Project.");
                  console.error("Tray delete persist failed:", e);
                }
              }}
            />
          )}
        </div>

        <ValidationReport report={report} clashes={clashes} onToggleIgnore={onToggleIgnore} onSnapToCompliance={onSnapToCompliance} onSnapAll={onSnapAll} />
      </div>

      {/*
        Modals MUST live inside the fullscreen wrapper so they remain visible
        in browser fullscreen mode (otherwise the body is hidden behind the
        fullscreen element and modals appear "missing").
      */}
      <AIAuditModal open={auditOpen} onClose={() => setAuditOpen(false)} projectId={projectId} />
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      <OpeningsEditor
        open={openingsOpen}
        onClose={() => setOpeningsOpen(false)}
        openings={project.wall_openings || []}
        onChange={(list) => update({ wall_openings: list })}
      />

      {/* Right-click menu on 3D equipment boxes + tray meshes. */}
      <ContextMenu
        menu={ctxMenu && {
          x: ctxMenu.x, y: ctxMenu.y,
          items: ctxMenu.item ? [
            // Equipment menu
            { label: "Focus camera",
              onClick: () => setFocusTarget({ ...ctxMenu.item, _ts: Date.now() }) },
            { label: "Duplicate", hint: "+12 in",
              onClick: duplicateEquipment },
            ctxMenu.item.fed_from_tag
              ? { label: "Clear feeder link",
                  hint: `← ${ctxMenu.item.fed_from_tag}`,
                  onClick: clearSource }
              : { label: "Wire to feeder source…",
                  hint: "pick any equipment",
                  onClick: wireToSource },
            { label: "Delete", danger: true,
              onClick: deleteEquipment },
          ] : ctxMenu.seg ? [
            // Tray / fitting menu
            { label: "Focus camera",
              onClick: () => focusOnTray(ctxMenu.seg) },
            { label: "Duplicate", hint: "+12 in",
              onClick: duplicateTray },
            ...(ctxMenu.seg.kind !== "fitting" ? [
              { label: "Rotate 90° about centerline",
                hint: `${Number(ctxMenu.seg.rotation_axis_deg) === 90 ? "→ 0°" : "→ 90°"}`,
                onClick: () => rotateTray(ctxMenu.seg.id) },
              { label: "Suggest fitting to nearest tray",
                onClick: quickJoinTray },
              { label: "AI: Suggest conduit type",
                hint: "Claude · NEC",
                onClick: suggestConduit },
            ] : []),
            { label: "Delete", danger: true,
              onClick: deleteTray },
          ] : [],
        }}
        onClose={() => setCtxMenu(null)}
      />

      {/* Quick-join dialog — same one used in the Cables tab. */}
      <QuickJoinDialog
        open={!!quickJoinSrc}
        sourceSeg={quickJoinSrc}
        projectId={projectId}
        onClose={() => setQuickJoinSrc(null)}
        onJoined={(segs) => update && update({ cable_trays: segs })}
      />

      {/* Wire-to-feeder-source dialog — pick ANY placed equipment as source. */}
      <WireToSourceDialog
        open={!!wireSourceItem}
        item={wireSourceItem}
        projectId={projectId}
        candidates={project.equipment_list || []}
        onClose={() => setWireSourceItem(null)}
        onWired={(data) => {
          const list = (project.equipment_list || []).map(
            (x) => x.id === data.item.id ? data.item : x,
          );
          update && update({ equipment_list: list });
        }}
      />
      </div>
    </div>
  );
}
